import { Descriptions, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type NamirialSigningModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/NamirialSigningModuleWithPermissionsSpecs';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import NamirialSigningModuleActions from './NamirialSigningModuleActions';

export type NamirialSigningModuleProps = { module: NamirialSigningModuleWithPermissionsSpecsFragment };

const NamirialSigningModulePage = ({ module }: NamirialSigningModuleProps) => {
    const { t } = useTranslation(['namirialSigningModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('namirialSigningModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('namirialSigningModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<NamirialSigningModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('namirialSigningModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <CollapsibleWrapper defaultActiveKey={t('namirialSigningModuleDetails:subTitle')}>
                <Panel
                    key={t('namirialSigningModuleDetails:subTitle')}
                    header={t('namirialSigningModuleDetails:subTitle')}
                >
                    <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label={t('namirialSigningModuleDetails:content.endPoint')}>
                            {module.namirialSetting.endpoint}
                        </Descriptions.Item>
                    </Descriptions>
                </Panel>
            </CollapsibleWrapper>
        </ConsolePageWithHeader>
    );
};

export default NamirialSigningModulePage;
